import { computed, defineComponent, h, PropType, unref, watch } from "@vue/composition-api";
import { LivechatSubjectType } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import providers from "@/lib/api";
import { parseISO } from "@/lib/time";
import { getCollabLiveUrl } from "@/lib/urlHelpers";
import StreamView from "../../../components/StreamView";
import useConsume from "../../../hooks/useConsume";
import useUserBroadcastingUpdated from "../../../hooks/useUserBroadcastingUpdated";
import useSingleLiveChat from "../../hooks/useSingleLiveChat";

export default defineComponent({
  name: "ConsumeController",
  props: {
    user: { type: Object as PropType<UserPublicData>, required: true },
  },
  setup(props, { root, listeners, emit }) {
    const {
      loading,
      userList,
      joinData,
      setOnUserBroadcastingCB,
      clientServerTimeDiff,
      errorData,
      isBlockedForSession,
      endChat,
    } = useSingleLiveChat({ userId: props.user.id });

    const getLoggerContext = (): Record<string, any> => {
      return {
        joinData: joinData.value,
        errorData: errorData.value,
      };
    };

    const {
      usersToConsume,
      connecting: consumeConnecting,
      connectingState: consumeConnectingState,
      consumeStream,
      closeAllStreams,
    } = useConsume({ joinData, getLoggerContext });

    useUserBroadcastingUpdated(props.user.id, data => {
      if (data.broadcasting && data.subject.subjectType === LivechatSubjectType.COLLAB_LIVE) {
        root.$router.replace(getCollabLiveUrl(props.user, data.subject.subjectId));
      }
    });

    const connecting = computed(() => Object.values(consumeConnecting.value)[0] ?? false);
    const connectingState = computed(() => Object.values(consumeConnectingState.value)[0] ?? "");
    const producer = computed(() => joinData.value?.users.find(i => i.userId === props.user.id) ?? null);
    const dateStart = computed(() => {
      if (!producer.value) {
        return "";
      }
      return new Date(parseISO(producer.value.joinTime).getTime() + clientServerTimeDiff.value).toISOString();
    });

    const blockedCloseStream = async () => {
      isBlockedForSession.value = true;
      closeAllStreams();
      await endChat();
    };

    setOnUserBroadcastingCB(({ chatUserId, broadcasting }) => {
      broadcasting && joinData.value?.token ? consumeStream(chatUserId, joinData.value.token) : closeAllStreams();
    });

    const redirectIfProducerInCollab = async () => {
      const response = await providers.UserPublicDataProvider.search({ data: { id: [props.user.id] } });
      const updatedUser = response.result[0];
      if (
        updatedUser.publicLivechatUser &&
        updatedUser.publicLivechatUser.chat?.subject.subjectType === LivechatSubjectType.COLLAB_LIVE
      ) {
        root.$router.replace(getCollabLiveUrl(props.user, updatedUser.publicLivechatUser.chat.subject.subjectId));
      }
    };

    watch(joinData, (joinData, old) => {
      if (!joinData) return;
      const producer = joinData.users.find(i => i.userId === props.user.id);
      const oldProducer = old?.users.find(i => i.userId === props.user.id);
      if (producer?.broadcasting && !old) {
        consumeStream(producer.id, joinData.token);
      } else if (!producer && oldProducer) {
        closeAllStreams();
      } else if (!producer && !oldProducer) {
        redirectIfProducerInCollab();
      }
    });

    watch(errorData, () => {
      if (!errorData) return;
      emit("error", errorData.value);
    });

    return () =>
      h(StreamView, {
        props: {
          isOwner: false,
          produce: false,
          producer: props.user,
          me: joinData.value?.me,
          streamers: unref(usersToConsume),
          connecting: unref(connecting),
          connectingState: unref(connectingState),
          loading: unref(loading),
          userList: unref(userList),
          streamStartedDate: unref(dateStart),
          threadId: joinData.value?.threadId,
          token: joinData.value?.token,
          sessionId: joinData.value?.sessionId,
          showBlockedWarning: isBlockedForSession.value,
        },
        on: {
          close: listeners.close,
          blockedCloseStream: blockedCloseStream,
        },
      });
  },
});
